import { CircularProgress } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { styled } from 'styled-components';

import {
  Hospital,
  HospitalStudies,
  requestFileSizeOfStudy,
} from '../../../../shared/api/hscan/hospital';
import { deliveryFee as _deliveryFee } from '../../../../shared/env';
import {
  StudiesPrice,
  useRequestPay,
  useVerifyPayment,
} from '../../../../shared/hooks/hospitalHook';
import { usePay } from '../../../../shared/hooks/paymentHook';
import {
  calculatePrice,
  calculateTaxFreePrice,
  calculateVolumePriceWithFileSize,
  calculateVolumeTaxFreePriceWithFileSize,
  isExpectablePriceModel,
  isVolumePriceModel,
} from '../../../../shared/utils/price';
import { BlueRoundedButtonWithIcon } from '../../common/components/BlueRoundedButton';
import {
  CheckboxWithText,
  CheckboxText,
} from '../../common/components/CheckboxWithText';
import { Loading } from '../../common/components/Loading';
import {
  AddresseeSlots,
  AddressForm,
  InvalidState,
} from '../../common/components/Slot/AddresseeSlots';
import {
  ModalTextContainer,
  WideModal,
} from '../../common/components/WideModal';
import {
  SectionContentContainer,
  SectionHeading,
  PriceTableTop,
} from '../../common/components/styledComponents';
import { whiteCircularProgressStyle } from '../../common/styles';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

import { PaymentTable } from './PaymentTable';

const PriceSummaryContent = styled.div`
  display: contents;

  ${p =>
    p.theme.isMobile &&
    `
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 36px;
    padding-top: 32px;
    padding-right: 20px;
    padding-bottom: 32px;
    padding-left: 20px;
    border-radius: 8px;
    background: white;
    `}
`;

const AlertMessage = styled.span`
  font-size: 12px;
  color: var(--HPACS_Text_Light_Grey, #a3a6ab);
`;

const PaymentAlertMessage = styled.div`
  margin-top: 32px;
  color: ${p => (p.theme.isMobile ? '#a3a6ab' : '#7a7a7e')};
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  word-break: keep-all;
`;
const initialInvalidState: InvalidState = {
  address: false,
  name: false,
  phone: false,
};
const initialPaymentForm = {
  address: { baseAddress: '', detailAddress: '', postalCode: '' },
  name: '',
  phone: '',
};
interface PaymentSectionProps {
  /**
   * 현재 검색한 병원
   */
  hospital: Hospital | null;
  /**
   * 입력한 주민번호
   */
  rrNumber: string;
  /**
   * 병원에서 선택한 검사들
   */
  hospitalStudies: HospitalStudies;
  /**
   * 검색 여부
   */
  searched: boolean;
  onSuccessPayment: () => void;
  /**
   * 예약 전달할 의료기관 (optional)
   */
  destGatewayId?: string;
  checkTerm: boolean;
  setCheckTerm: (checked: boolean) => void;
  pricePerHospital: Record<string, StudiesPrice>;
  setPricePerHospital: React.Dispatch<
    React.SetStateAction<Record<string, StudiesPrice>>
  >;
  isRequestFormValid: boolean;
}
export const PaymentSection = ({
  hospital,
  rrNumber,
  hospitalStudies,
  onSuccessPayment,
  searched,
  destGatewayId,
  checkTerm,
  setCheckTerm,
  pricePerHospital,
  setPricePerHospital,
  isRequestFormValid,
}: PaymentSectionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.payment',
  });
  const windowSize = useContext(WindowSizeContext);
  const isMobile = windowSize === WindowSize.MOBILE;

  /* cd 배송받기 */
  const [withCd, setWithCd] = useState<boolean>(false);

  const [form, setForm] = useState<AddressForm>(initialPaymentForm);

  const {
    useMailPerHospital,
    mailingMandatoryPerHospital,
    reset: resetHospitalMailingProperties,
  } = useAggregatedHospitalMailingProperties({ hospital, searched });

  /* study가 변경되거나 등기 배송 여부가 변경되면 다시 check하도록 */
  useEffect(() => {
    setCheckTerm(false);
  }, [hospitalStudies, setCheckTerm, withCd]);

  const isNothingSelected = useMemo(() => {
    return (
      Object.values(hospitalStudies).flatMap(studies => studies).length === 0
    );
  }, [hospitalStudies]);

  /* 선택한 병원이 아무것도 없으면 cd 를 false 로 만듬 */
  useEffect(() => {
    if (isNothingSelected) {
      setWithCd(false);
    }
  }, [isNothingSelected]);

  const { forceCdByHospital, disabledWithCd } = useForceCdByHospital(
    hospitalStudies,
    useMailPerHospital,
    mailingMandatoryPerHospital,
  );

  const {
    isLoading: isLoadingFileSize,
    isError: isErrorFileSize,
    state: fileSizeByStudyInstanceUID,
  } = useStudiesFileSize({
    enabled: hospital ? isExpectablePriceModel(hospital?.price) : false,
    hospitalId: hospital?.id ?? '',
    hospitalStudies,
  });

  /*
  PaymentTable 에 나타날 항목을 수정하는 effect
  PaymentTable Row 변동 원인
  1. 병원에서 검색 후 스터디 선택
  */
  useEffect(() => {
    // 어느 병원에서도 선택된 스터디가 없으면
    if (isNothingSelected) {
      setPricePerHospital({}); // 가격 테이블도 비운다
      return;
    }

    // 1. 현재 병원이 선택돼있고 이 병원으로 검색된 적 있으면 선택된 스터디를 가격 테이블에 추가한다
    if (hospital && searched) {
      const priceModel = hospital.price;
      const hospitalId = hospital.id;

      const studiesInHospital = hospitalStudies[hospitalId] ?? [];
      const numStudies = studiesInHospital.length ?? 0;

      if (hospitalId && studiesInHospital && numStudies) {
        if (
          isVolumePriceModel(priceModel) &&
          isExpectablePriceModel(priceModel)
        ) {
          const totalSize = isLoadingFileSize
            ? undefined
            : studiesInHospital.reduce(
                (totalFileSize, study) =>
                  totalFileSize +
                  (fileSizeByStudyInstanceUID[study.studyInstanceUID] ?? 0),
                0,
              );
          setPricePerHospital(prev => ({
            ...prev,
            [hospitalId]: {
              hospitalName: hospital.name,
              numStudies,
              price: totalSize
                ? calculateVolumePriceWithFileSize(priceModel, totalSize)
                : NaN,
              taxFree: totalSize
                ? calculateVolumeTaxFreePriceWithFileSize(priceModel, totalSize)
                : NaN,
              priceModel,
              isPriceLoading: isLoadingFileSize,
              isPriceError: isErrorFileSize,
              size: totalSize,
            },
          }));
        } else {
          setPricePerHospital(prev => ({
            ...prev,
            [hospitalId]: {
              hospitalName: hospital.name,
              numStudies,
              price: calculatePrice(priceModel, hospitalStudies[hospitalId]),
              taxFree: calculateTaxFreePrice(
                priceModel,
                hospitalStudies[hospitalId],
              ),
              priceModel,
            },
          }));
        }
      } else {
        setPricePerHospital(prev => {
          const next = { ...prev };
          delete next[hospital.id];
          return next;
        });
      }
    }
  }, [
    isNothingSelected,
    hospital,
    hospitalStudies,
    searched,
    isLoadingFileSize,
    isErrorFileSize,
    fileSizeByStudyInstanceUID,
    setPricePerHospital,
  ]);

  const deliveryFee = useMemo(
    () =>
      withCd &&
      Object.entries(hospitalStudies).filter(
        ([hospitalId, studies]) =>
          studies.filter(study => study.status !== 'FAILED').length !== 0 &&
          useMailPerHospital[hospitalId],
      ).length !== 0
        ? _deliveryFee
        : 0,
    [hospitalStudies, useMailPerHospital, withCd],
  );
  const totalPrice = useMemo(
    () =>
      Object.values(pricePerHospital)
        .map(studiesPrice => studiesPrice.price)
        .reduce((priceSum, price) => priceSum + price, 0) + deliveryFee,
    [deliveryFee, pricePerHospital],
  );

  const onSuccessPay = () => {
    setPricePerHospital({});
    onSuccessPayment();
    resetHospitalMailingProperties();
  };
  const { isLoading, isPending, isRequestPayPending, requestPay } =
    usePaymentSectionPay({
      totalPrice,
      paymentName: `${t('name')}`,
      onSuccessPay,
      onSuccessVerify: () => setOpenModal(true),
    });

  const [invalidState, setInvalidState] =
    useState<InvalidState>(initialInvalidState);

  const handleClickSubmitButton = () => {
    setInvalidState(() => initialInvalidState);
    const [invalidProperties, newInvalidState] = validatePaymentSectionForm(
      withCd,
      form,
    );
    if (invalidProperties.length > 0) {
      setInvalidState(newInvalidState);
      return;
    }

    /* 발급 신청 및 결제 준비 요청 */
    if (hospital?.id && hospitalStudies) {
      requestPay({
        body: {
          mailingAddress: form.address,
          mailingIncluded: withCd,
          price: totalPrice,
          requestStudies: hospitalStudies,
          destGatewayId: destGatewayId,
        },
        registration: rrNumber ? rrNumber : undefined,
      });
    }
  };

  const [openModal, setOpenModal] = useState<boolean>(false);
  return (
    <>
      <SectionContentContainer>
        <SectionHeading>{t('heading')}</SectionHeading>
        <PriceSummaryContent>
          <PriceTableTop
            left={
              <CheckboxWithText
                text={
                  <CheckboxText>
                    {isMobile
                      ? t('mobileMailingIncluded')
                      : t('mailingIncluded')}
                  </CheckboxText>
                }
                disabled={disabledWithCd}
                value={forceCdByHospital || withCd}
                onChange={setWithCd}
              />
            }
            right={<AlertMessage>{t('cdAlert')}</AlertMessage>}
          />
          <PaymentTable
            useMailPerHospital={useMailPerHospital}
            pricePerHospital={pricePerHospital}
            total={totalPrice}
            withCd={forceCdByHospital || withCd}
            deliveryFee={deliveryFee}
          />
          {(forceCdByHospital || withCd) && (
            <AddresseeSlots
              invalidState={invalidState}
              onChangeForm={setForm}
              form={form}
            />
          )}
          <PaymentAlertMessage>
            <Trans
              t={t}
              i18nKey={isMobile ? 'mobilePaymentAlert' : 'paymentAlert'}
              components={[<br />]}
            />
          </PaymentAlertMessage>
          <CheckboxWithText
            text={<CheckboxText>{t('checkAll')}</CheckboxText>}
            $margin="18px 0 0 0"
            value={checkTerm}
            onChange={setCheckTerm}
            disabled={!Object.values(pricePerHospital).length}
          />
          <BlueRoundedButtonWithIcon
            marginTop="43px"
            onClick={isRequestPayPending ? undefined : handleClickSubmitButton}
            disabled={!isRequestFormValid || isLoadingFileSize}>
            {isRequestPayPending ? (
              <CircularProgress size={24} style={whiteCircularProgressStyle} />
            ) : (
              t('submitButton')
            )}
          </BlueRoundedButtonWithIcon>
        </PriceSummaryContent>
      </SectionContentContainer>
      <PaymentConfirmModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        isLoading={isLoading}
      />
      {!isLoading || !isPending || <Loading />}
    </>
  );
};

interface PaymentConfirmModalProps {
  openModal: boolean;
  setOpenModal(open: boolean): void;
  isLoading: boolean;
}
const PaymentConfirmModal = ({
  isLoading,
  openModal,
  setOpenModal,
}: PaymentConfirmModalProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.payment',
  });

  return (
    <WideModal
      open={openModal}
      onClose={() => {
        if (!isLoading) {
          setOpenModal(false);
        }
      }}
      width="1080px">
      <ModalTextContainer>
        <Trans
          t={t}
          i18nKey="modal.confirmMessage"
          components={[<br />, <strong />]}
        />
      </ModalTextContainer>
      {!isLoading && (
        <BlueRoundedButtonWithIcon
          marginTop="22px"
          onClick={() => {
            navigate('/requested-images');
            setOpenModal(false);
          }}>
          {t('modal.confirmButton')}
        </BlueRoundedButtonWithIcon>
      )}
    </WideModal>
  );
};

interface PaymentSectionPayProps {
  totalPrice: number;
  paymentName: string;
  onSuccessPay: () => void;
  onSuccessVerify: () => void;
}
const usePaymentSectionPay = ({
  totalPrice,
  paymentName,
  onSuccessPay,
  onSuccessVerify,
}: PaymentSectionPayProps) => {
  const { mutate: verifyPayment } = useVerifyPayment({
    onSuccess: onSuccessVerify,
  });

  const { isLoading, isPending, pay } = usePay({
    paymentName,
    onSuccessPay: (merchantId: string) => {
      verifyPayment(merchantId);
      onSuccessPay();
    },
    mobileRedirectUrlPath: '/mobile-payment-callback', // FIXME: 나중에 빼기
  });

  const { isPending: isRequestPayPending, mutate: requestPay } = useRequestPay({
    onSuccess(payment) {
      const merchantId = payment.id;
      if (merchantId) {
        if (!totalPrice) {
          verifyPayment(merchantId);
          onSuccessPay();
        } else {
          pay(merchantId, payment.price.totalFee, payment.price.taxFree);
        }
      }
    },
  });

  return {
    isLoading,
    isPending,
    isRequestPayPending,
    requestPay,
  };
};

const useForceCdByHospital = (
  hospitalStudies: HospitalStudies,
  useMailPerHospital: Record<string, boolean>,
  mailingMandatoryPerHospital: Record<string, boolean>,
) => {
  const hospitalIds = useMemo(
    () =>
      Object.entries(hospitalStudies)
        .filter(([_, studies]) => !!studies.length)
        .map(([hospitalId, _]) => hospitalId),
    [hospitalStudies],
  );
  const listedMandatoryPerHospital = useMemo(
    () =>
      Object.entries(mailingMandatoryPerHospital).filter(([hospitalId, _]) =>
        hospitalIds.includes(hospitalId),
      ),
    [hospitalIds, mailingMandatoryPerHospital],
  );

  const listedUseMailPerHospital = useMemo(
    () =>
      Object.entries(useMailPerHospital).filter(([hospitalId, _]) =>
        hospitalIds.includes(hospitalId),
      ),
    [hospitalIds, useMailPerHospital],
  );

  const forceCdByHospital = useMemo(() => {
    if (
      listedMandatoryPerHospital
        .map(([_, mailingMandatory]) => mailingMandatory)
        .includes(true)
    ) {
      return true;
    }

    if (
      !listedUseMailPerHospital.map(([_, useMail]) => useMail).includes(true)
    ) {
      return false;
    }

    return undefined;
  }, [listedMandatoryPerHospital, listedUseMailPerHospital]);

  const disabledWithCd = forceCdByHospital !== undefined;

  return { forceCdByHospital, disabledWithCd };
};

export const validatePaymentSectionForm = (
  withCd: boolean,
  form: AddressForm,
): [string[], InvalidState] => {
  if (!withCd) {
    return [[], initialInvalidState];
  }
  const newInvalidState = { ...initialInvalidState };
  const invalidProperties = [];
  if (form.name === '') {
    newInvalidState.name = true;
    invalidProperties.push('receiver.name');
  }
  if (form.phone === '') {
    newInvalidState.phone = true;
    invalidProperties.push('receiver.phone');
  }
  if (
    form.address.baseAddress === '' ||
    form.address.detailAddress === '' ||
    form.address.postalCode === ''
  ) {
    newInvalidState.address = true;
    invalidProperties.push('receiver.address');
  }
  return [invalidProperties, newInvalidState];
};

interface UseAggregatedHospitalMailingPropertiesParams {
  hospital: Hospital | null;
  searched: boolean;
}
const useAggregatedHospitalMailingProperties = ({
  hospital,
  searched,
}: UseAggregatedHospitalMailingPropertiesParams) => {
  const [useMailPerHospital, setUseMailPerHospital] = useState<
    Record<string, boolean>
  >({});
  const [mailingMandatoryPerHospital, setMailingMandatoryPerHospital] =
    useState<Record<string, boolean>>({});

  /* hostpital 에 따라 메일 가능 여부 저장해둠 */
  useEffect(() => {
    if (hospital?.id && searched) {
      if (!Object.keys(useMailPerHospital).includes(hospital.id)) {
        setUseMailPerHospital(prev => ({
          ...prev,
          [hospital.id]: hospital.useMailing,
        }));
      }

      if (!Object.keys(mailingMandatoryPerHospital).includes(hospital.id)) {
        setMailingMandatoryPerHospital(prev => ({
          ...prev,
          [hospital.id]: hospital.mailingMandatory,
        }));
      }
    }
  }, [
    hospital?.id,
    hospital?.useMailing,
    hospital?.mailingMandatory,
    searched,
    useMailPerHospital,
    mailingMandatoryPerHospital,
  ]);

  const reset = () => {
    setUseMailPerHospital({});
    setMailingMandatoryPerHospital({});
  };

  return { useMailPerHospital, mailingMandatoryPerHospital, reset };
};

const REQUESTED = -1;
const ERROR = -2;

interface UseStudiesFileSizeParams {
  enabled: boolean;
  hospitalId: string;
  hospitalStudies: HospitalStudies;
}
const useStudiesFileSize = ({
  enabled,
  hospitalId,
  hospitalStudies,
}: UseStudiesFileSizeParams) => {
  const studies = useMemo(() => {
    return Object.values(hospitalStudies)
      .flatMap(_ => _)
      .sort((a, b) => (a.studyInstanceUID > b.studyInstanceUID ? 1 : 0));
  }, [hospitalStudies]);

  /* [study instance uid]: size */
  const [state, setState] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setState(prev => {
      const uids = Object.entries(prev)
        .filter(([, price]) => price === undefined || price <= 0)
        .map(([uid]) => uid);
      const validUids = Object.values(hospitalStudies)
        .flatMap(_ => _)
        .map(study => study.studyInstanceUID)
        .filter(studyInstanceUID => prev[studyInstanceUID] !== undefined);
      const invalidUids = uids.filter(uid => !validUids.includes(uid));
      if (invalidUids.length === 0) {
        return prev;
      }
      const next = { ...prev };
      invalidUids.forEach(uid => {
        delete next[uid];
      });
      return next;
    });
  }, [hospitalStudies]);

  // update file size
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const noData = studies.filter(
      study => state[study.studyInstanceUID] === undefined,
    );
    if (noData.length === 0) {
      return;
    }
    setIsError(false);
    setIsLoading(true);
    Promise.allSettled(
      noData.map(study => {
        setState(prev => ({ ...prev, [study.studyInstanceUID]: REQUESTED }));
        return requestFileSizeOfStudy({
          id: hospitalId,
          studyInstanceUID: study.studyInstanceUID,
        }).then<[string, number]>(({ data: size }) => {
          return [study.studyInstanceUID, size];
        });
      }),
    ).then(fullfilled => {
      const rejectedIndices = fullfilled.flatMap(
        (result, index): [number] | [] => {
          if (result.status !== 'rejected') {
            return [];
          }
          return [index];
        },
      );
      if (rejectedIndices.length) {
        setIsError(true);
        setState(prev => {
          const update = rejectedIndices
            .map(index => noData[index].studyInstanceUID)
            .reduce<Record<string, number>>((map, studyInstanceUID) => {
              map[studyInstanceUID] = ERROR;
              return map;
            }, {});
          return { ...prev, ...update };
        });
      }
      setState(prev => {
        const newOne = fullfilled
          .filter(
            (f): f is PromiseFulfilledResult<[string, number]> =>
              f.status === 'fulfilled',
          )
          .map(s => s.value)
          .reduce(
            (m, [uid, size]) =>
              Object.assign(m, {
                [uid]: size,
              }),
            {},
          );

        return {
          ...prev,
          ...newOne,
        };
      });
      setIsLoading(false);
    });
  }, [enabled, hospitalId, studies, state]);

  return {
    state,
    isLoading,
    isError,
  };
};
